// Installation identity is captured before page navigation changes metadata.
// It is a per-window session value, never an origin-wide "current app" value.
export const PWA_QUERY = 'pwa'
export const PWA_SESSION_KEY = 'freshman-pwa-installation-v1'
export const DEVICE_SCOPE_PREFIX = '/_device-notifications/'

const aliases = Object.assign(Object.create(null), { w:'wordbase', capitals:'lettercomb', selfchat:'chat' })
const domains = Object.assign(Object.create(null), {
  'wordbase.app':'wordbase', 'dinder.social':'dinder', 'pico-repo.com':'pico-repo',
  'tally.gallery':'tally', 'crowdmeal.app':'crowdmeal', 'matchbox.app':'matchbox',
  'grtr.xyz':'greeter', 'grtr.app':'greeter', 'greeter.social':'greeter',
  'web-app-store.com':'web-app-store', 'vibe.photos':'vibe', 'spo.tu.fo':'spot',
  'tr.ink':'et', 'buystream.app':'buystream', 'basin.fish':'fishbowl',
})
const sharedPages = new Set(['', 'home', 'download', 'notify', 'settings', 'login', 'u', 'profile', 'reset'])
export const canonicalInstall = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !/^[a-z][a-z0-9_-]{0,63}$/.test(value)) return undefined
  return aliases[value] || value
}
export function installFromStartUrl(value: string, origin: string): string {
  const start = new URL(value || '/', origin)
  const hostApp = domains[start.hostname] || (start.hostname.endsWith('.freshman.dev') && start.hostname.split('.')[0] !== 'www'
    ? canonicalInstall(start.hostname.split('.')[0]) : undefined)
  let page = start.pathname.replace(/^\/-?/, '').split('/')[0]
  if (page === 'misc') page = start.pathname.split('/')[2]
  return sharedPages.has(page) ? (hostApp || 'base') : canonicalInstall(page) || hostApp || 'base'
}
export const standalonePwa = () => Boolean(window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone)

export function captureInstallation(href: string, standalone: boolean, storage?: Pick<Storage, 'getItem' | 'setItem'>) {
  const launch = new URL(href)
  let stored: { key?:string, launch?:string }
  try { stored = JSON.parse(storage?.getItem(PWA_SESSION_KEY) || 'null') } catch {}
  const previous = canonicalInstall(stored?.key)
  const marker = canonicalInstall(launch.searchParams.get(PWA_QUERY))
  // A link visited inside an already open Home Screen app cannot change it.
  const key = standalone && previous ? previous : marker || previous
  const identity = { key, launch: stored?.launch || launch.href, legacy: standalone && !key }
  if (key) {
    try { storage?.setItem(PWA_SESSION_KEY, JSON.stringify({ key, launch:identity.launch })) } catch {}
  }
  return identity
}
let session: Storage
try { session = window.sessionStorage } catch {}
let identity = captureInstallation(location.href, standalonePwa(), session)
export const getPwaInstallation = () => ({ ...identity, key:identity.key || (identity.legacy ? undefined : 'base') })
export function choosePwaInstallation(key: string) {
  key = canonicalInstall(key)
  if (!key) throw Error('Choose the Home Screen app you opened.')
  // Session storage must work so reloads retain an old install's explicit choice.
  session.setItem(PWA_SESSION_KEY, JSON.stringify({ key, launch:identity.launch }))
  identity = { ...identity, key, legacy:false }
}
export const deviceWorkerScope = (key: string) => `${DEVICE_SCOPE_PREFIX}${canonicalInstall(key) || 'base'}/`

export function installationManifest<T extends { start_url?: string, id?: string, scope?:string }>(value: T, origin=location.origin): T & { id?:string, scope?:string } {
  if (!value || !value.start_url) return value
  let start: URL
  try { start = new URL(value.start_url, origin) } catch { return value }
  if (start.origin !== origin) return value
  const key = identity.key || installFromStartUrl(start.href, origin)
  // Preserve the exact start route/query/hash, including custom-domain and
  // professional routes. ID is stable even while a game's icon/name changes.
  start.searchParams.set(PWA_QUERY, key)
  const hostApp = installFromStartUrl('/', origin)
  const id = new URL(key === 'base' || key === hostApp ? '/' : `/${key}`, origin).href
  return { ...value, id, start_url:start.href, scope:new URL('/', origin).href }
}
